import { getAllowedDeductibilityForPlanType } from '@Common/bo/b2b_plan_deductibility';
import { isPlanEligibleForCampaignChoices } from '@Common/helpers/b2b_campaigns';
import {
  B2bPlanCategoryEsPerecoiValues,
  B2bSavingPlanDeductibility,
  B2bSavingPlanDeductibilityName,
  B2bSavingPlanType,
} from '@Common/types/b2b/plans';
import { B2bCashSource } from '@Common/types/bo/b2b/b2b';
import { B2bCampaignType } from '@Common/types/bo/b2b/campaigns';
import { B2bContractType } from '@Common/types/bo/b2b/contracts';
import { getPlanTypeName } from '@Common/types/bo/b2b/plans';

/** Names of the non-plan rows of the allocation file "Dispositif*" column. The template and the file upload use them. */
export const PAYOUT_PLAN_NAME = 'Versement compte bancaire';
export const CCB_PLAN_NAME = 'CCB';

type OptionSharesPlan = {
  id: number;
  type: B2bSavingPlanType;
  plan_label?: string | null;
};

type OptionProduct = {
  b2b_contract_id: number;
  contract_type: B2bContractType;
  cash_sources: B2bCashSource[];
  shares_plans: OptionSharesPlan[];
};

export type CampaignPlanOption<Product extends OptionProduct> = {
  /** Allocation choice id: unique for each plan and deductibility */
  id: string;
  /** Name in the allocation file "Dispositif*" column and in the review step plan select */
  name: string;
  product: Product;
  sharesPlan: Product['shares_plans'][number];
  deductibility: B2bSavingPlanDeductibility | null;
};

export const generatePlanAllocationChoiceId = (
  planId: number,
  deductibility: B2bSavingPlanDeductibility | null | undefined,
) => (deductibility ? `plan_${planId}_${deductibility}` : `plan_${planId}`);

function optionDeductibilities(
  planType: B2bSavingPlanType,
  campaignType: B2bCampaignType,
): (B2bSavingPlanDeductibility | null)[] {
  if (
    campaignType === 'voluntary_deposit' &&
    B2bPlanCategoryEsPerecoiValues.some((perecoiValue) => planType.includes(perecoiValue))
  ) {
    return getAllowedDeductibilityForPlanType(planType);
  }
  return [null];
}

/**
 * Returns the plan options of a campaign with company collected choices.
 * The allocation file template, the file upload and the review step plan select use this function.
 * This makes sure that they show and accept the same plan names.
 * The name is the plan type name, the deductibility and the plan label.
 * Two or more options can have the same name. In this condition, the plan id is added at the end.
 */
export function buildCampaignPlanOptions<Product extends OptionProduct>({
  products,
  campaignType,
  matchingContributionRelatedCashSource,
}: {
  products: Product[];
  campaignType: B2bCampaignType;
  matchingContributionRelatedCashSource: B2bCashSource | null | undefined;
}): CampaignPlanOption<Product>[] {
  const options = products
    .filter((product) =>
      isPlanEligibleForCampaignChoices(
        product,
        campaignType,
        matchingContributionRelatedCashSource,
      ),
    )
    .flatMap((product) =>
      product.shares_plans
        // ARTICLE plans are never selectable, regardless of campaign type
        .filter((sharesPlan) => !sharesPlan.type.includes('ARTICLE'))
        .flatMap((sharesPlan) =>
          optionDeductibilities(sharesPlan.type, campaignType).map((deductibility) => {
            const typeName = getPlanTypeName(sharesPlan.type, product.contract_type);
            const deductibilityName = deductibility
              ? ` ${B2bSavingPlanDeductibilityName[deductibility]}`
              : '';
            const labelName = sharesPlan.plan_label ? ` (${sharesPlan.plan_label})` : '';

            return {
              product,
              sharesPlan,
              deductibility,
              baseName: `${typeName}${deductibilityName}${labelName}`,
            };
          }),
        ),
    );

  const baseNameCounts = new Map<string, number>();
  options.forEach(({ baseName }) => {
    baseNameCounts.set(baseName, (baseNameCounts.get(baseName) ?? 0) + 1);
  });

  return options.map(({ product, sharesPlan, deductibility, baseName }) => ({
    id: generatePlanAllocationChoiceId(sharesPlan.id, deductibility),
    name: (baseNameCounts.get(baseName) ?? 0) > 1 ? `${baseName} (${sharesPlan.id})` : baseName,
    product,
    sharesPlan,
    deductibility,
  }));
}
